import type { InputFieldMetadata, InputFieldType, InputMetadata } from "@charterindex/halyard";
import type { Disposable } from "../util/lifecycle";
import type { EventCallback, EventType } from "../util/event-manager";
import type { StringKey } from "../util/type";
import { notify, observable } from "ecce-react";
import { EventManager } from "../util/event-manager";
import { EditorField } from "./editor-field";


type EditorEvents<T> = {
	change: { editor: Editor<T> }
};

export type EditorConfig<T> = {
	/**
	 * The initial data for the Editor.
	 */
	data: Partial<T>;
	/**
	 * The InputMetadata describing the GraphQL Input.
	 */
	metadata: InputMetadata<T>;
};
/**
 * Manages the state of a {@link InputMetadata} edit.
 *
 * Creates a {@link EditorField} for each value of a GraphQL mutation attributes
 * input. These fields can then be rendered into a Form using the
 * `<FormFieldControl/>` component.
 */
export class Editor<T> implements Disposable {
	readonly #metadata: InputMetadata<T>;
	readonly #fields: ReadonlyMap<string, EditorField<InputFieldType>>;
	#allValues: T | null = null;
	#changedValues: Partial<T> | null = null;

	readonly #events = new EventManager<EditorEvents<T>>();

	constructor(config: EditorConfig<T>) {
		this.#metadata = config.metadata;

		const fields = new Map<string, EditorField<InputFieldType>>();
		for(const fieldName of Object.keys(this.#metadata)) {
			const fieldMetadata = (this.#metadata as Record<string, InputFieldMetadata>)[fieldName]!;
			const fieldValue = (config.data as Record<string, any>)[fieldName]; // eslint-disable-line @typescript-eslint/no-explicit-any
			fields.set(fieldName, new EditorField(this, fieldMetadata, fieldValue));
		}
		this.#fields = fields;
	}


	/**
	 * Set the data for the Editor, resetting the `changed` state of each field.
	 */
	resetData(data: Partial<T>): void {
		this.#events.pause();
		try {
			for(const fieldName of Object.keys(this.#metadata)) {
				const nextValue = data[fieldName as keyof T];
				this.field<any>(fieldName as StringKey<T>)["resetValue"](nextValue ?? null); // eslint-disable-line @typescript-eslint/no-explicit-any
			}

			this.#events.resume();
			this.#events.notify("change", { editor: this });
		} finally {
			this.#events.resume();
		}
	}

	/**
	 * Update some of this Editor's fields. Only dispatches a single `change` event.
	 */
	patchData(data: Partial<T>): void {
		this.#events.pause();
		try {
			for(const fieldName of Object.keys(data)) {
				const nextValue = data[fieldName as keyof T];
				this.field<any>(fieldName as StringKey<T>)?.setValue(nextValue ?? null); // eslint-disable-line @typescript-eslint/no-explicit-any
			}

			this.#events.resume();
			this.#events.notify("change", { editor: this });

	 	} finally {
			this.#events.resume();
		}
	}

	/**
	 * Get the {@link EditorField} with the specified name.
	 *
	 * @template Value _Assert_ the type of the field.
	 * @throws if the Editor has no field with the specified name.
	 */
	field<Value extends InputFieldType = InputFieldType>(name: StringKey<T>): EditorField<Value> {
		const field = this.#fields.get(name);
		if(!field) {
			throw new Error(`No such field: "${name}"`);
		}

		return field as EditorField<Value>;
	}

	/**
	 * Test if this Editor has a field with the specified name.
	 */
	has(fieldName: StringKey<T>): boolean;
	has(fieldName: string): boolean;
	has(fieldName: string): boolean {
		return this.#fields.has(fieldName);
	}

	/**
	 * Called by `EditorField` when it's value changes.
	 */
	private handleFieldChanged() {
		this.#changedValues = null;
		this.#allValues = null;

		notify(this, "changed");
		notify(this, "changedValues");
		notify(this, "allValues");
		this.#events.notify("change", { editor: this });
	}

	/**
	 * Get only the values of the Editor which have been changed.
	 *
	 * Is @observable().
	 */
	@observable()
	get changedValues(): T {
		if(!this.#changedValues) {
			const nextValue: Record<string, unknown> = {};
			for(const field of this.#fields.values()) {
				if(field.changed) {
					nextValue[field.name] = field.value;
				}
			}

			this.#changedValues = nextValue as T;
		}
		return this.#changedValues as T;
	}

	/**
	 * Get all the values of the Editor, regardless of whether they have been
	 * changed.
	 *
	 * Is @observable().
	 */
	@observable()
	get allValues(): T {
		if(!this.#allValues) {
			const nextValue: Record<string, unknown> = {};
			for(const field of this.#fields.values()) {
				if(field.value === null && !field.changed) {
					continue;
				}

				nextValue[field.name] = field.value;
			}

			this.#allValues = nextValue as T;
		}

		return this.#allValues;
	}

	/**
	 * Test if any of the editor's fields have been changed.
	 */
	@observable()
	get changed(): boolean {
		for(const field of this.#fields.values()) {
			if(field.changed) {
				return true;
			}
		}

		return false;
	}

	get fields(): Iterable<EditorField<InputFieldType>> {
		return this.#fields.values();
	}

	on<E extends EventType<EditorEvents<T>>>(ev: E, callback: EventCallback<EditorEvents<T>, E>): this {
		this.#events.on(ev, callback);
		return this;
	}

	off<E extends EventType<EditorEvents<T>>>(ev: E, callback: EventCallback<EditorEvents<T>, E>): this {
		this.#events.off(ev, callback);
		return this;
	}

	dispose(): void {
		this.#events.dispose();
	}
}
